import { RestError } from '@azure/core-http';
import { KnownGroupType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { castDraft } from 'immer';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useImmer } from 'use-immer';
import { downloadToBrowser } from '../../../../../core/util/download-to-browser.util';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { DropdownOption } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { useQueryGetCarrierHierarchy } from '../../../../carriers/carriers.queries';
import { useQueryListGroups } from '../../../../groups/groups.queries';
import { useMutationListAllCarrierCouriers } from '../../../couriers.queries';
import { makeCourierCsvExport, resolveBatchDefaultCarrierID } from '../../../logic/courier-batch.map';

export interface BatchExportCouriersActionProps {
  projectID: string;
  onAbort(): void;
  onSuccess(): void;
}

export interface BatchExportCouriersFormState {
  groupID: string;
}

export type BatchExportCouriersActionState = {
  exporting: boolean;
  error?: RestError | Error;
};

export function useBatchExportCouriersAction({ projectID, onAbort, onSuccess }: BatchExportCouriersActionProps) {
  const [state, setState] = useImmer<BatchExportCouriersActionState>({ exporting: false });

  const { control, handleSubmit, setValue, getValues } = useForm<BatchExportCouriersFormState>({
    defaultValues: { groupID: '' }
  });

  const carrierGroups = useQueryListGroups(projectID, false, true, true, [KnownGroupType.Carriers]);

  const unitMenuItems: DropdownOption<string>[] = useMemo(
    () =>
      naturalSort((carrierGroups.data || []).map((group) => ({ label: group.name, value: group.id }))).asc(
        (option) => option.label
      ),
    [carrierGroups.data]
  );

  // The hierarchy names the carrier the operator is on, to preselect.
  const reached = useQueryGetCarrierHierarchy(projectID);
  const defaultGroupID = resolveBatchDefaultCarrierID(reached.data);

  // The default arrives with the hierarchy, after the form has mounted. Only one the dropdown offers is
  // preselected — any other would leave the field looking empty while still holding a value.
  useEffect(() => {
    const isOffered = unitMenuItems.some((option) => option.value === defaultGroupID);
    if (defaultGroupID && isOffered && !getValues('groupID')) {
      setValue('groupID', defaultGroupID);
    }
  }, [defaultGroupID, unitMenuItems, getValues, setValue]);

  const listCouriersMutation = useMutationListAllCarrierCouriers();

  const handlers = {
    onCancel: onAbort,
    async onSubmit({ groupID }: BatchExportCouriersFormState) {
      if (!groupID || state.exporting) {
        return;
      }

      setState((draft) => {
        draft.exporting = true;
        draft.error = undefined;
      });

      try {
        const couriers = await listCouriersMutation.mutateAsync({ groupID });
        if (couriers.length === 0) {
          setState((draft) => {
            draft.exporting = false;
            draft.error = new Error('This carrier has no couriers to export. Choose another carrier.');
          });
          return;
        }
        downloadToBrowser(makeCourierCsvExport(couriers), 'GroundfloorCourierExport.csv', 'text/csv');
        onSuccess();
      } catch (error) {
        setState((draft) => {
          draft.exporting = false;
          draft.error = castDraft(error as RestError);
        });
      }
    }
  };

  return {
    state,
    handlers,
    control,
    handleSubmit,
    unitMenuItems,
    loading: carrierGroups.isLoading || reached.isLoading
  } as const;
}
